import { useEffect, useRef, useState } from "react";

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || "";
const SCRIPT_SRC = "https://challenges.cloudflare.com/turnstile/v0/api.js";

let scriptLoadPromise = null;
function loadTurnstileScript() {
  if (scriptLoadPromise) return scriptLoadPromise;
  scriptLoadPromise = new Promise((resolve, reject) => {
    if (window.turnstile) { resolve(); return; }
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", reject);
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = reject;
    document.head.appendChild(script);
  });
  return scriptLoadPromise;
}

/**
 * Cloudflare Turnstile CAPTCHA widget.
 *
 * Renders nothing (and calls onVerify(null) once) when
 * REACT_APP_TURNSTILE_SITE_KEY isn't set — matches the backend's
 * CAPTCHA_PROVIDER=disabled dev/test no-op, so local development never
 * needs a real Turnstile account. In production, set
 * REACT_APP_TURNSTILE_SITE_KEY (frontend build arg) and CAPTCHA_PROVIDER=
 * turnstile + TURNSTILE_SECRET_KEY (backend) together — see captcha_utils.py.
 */
export default function Turnstile({ onVerify }) {
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!SITE_KEY) {
      onVerify(null);
      return;
    }
    let cancelled = false;
    loadTurnstileScript()
      .then(() => { if (!cancelled) setReady(true); })
      .catch(() => { if (!cancelled) onVerify(null); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!ready || !SITE_KEY || !containerRef.current || !window.turnstile) return;
    widgetIdRef.current = window.turnstile.render(containerRef.current, {
      sitekey: SITE_KEY,
      callback: (token) => onVerify(token),
      "expired-callback": () => onVerify(null),
      "error-callback": () => onVerify(null),
    });
    return () => {
      if (widgetIdRef.current != null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  if (!SITE_KEY) return null;
  return <div ref={containerRef} data-testid="turnstile-widget" className="my-2" />;
}
